import React, { useCallback, useMemo, useState } from 'react';
import { Anchor, Box, Grid, SearchInput, Text, useDebounce, VirtualTable } from '@common-fe/common-fe';

import { FilterSetters } from '@/utils/hooks/useFilters';

import { ReportTab, ReportTableBaseProps, ReportTypeKeys } from '../../report.types';
import { AddScheduledReportButton } from '../../ScheduledReport';
import EmployerSelector from '../EmployerSelector';
import ExportButton from '../ExportButton';
import { useIsEmployerIsPresent, useShowAppliedSettingsSnackbar } from '../hooks';
import PlaceholderBase from '../PlaceholderBase';
import ReportLastUpdateDate from '../ReportLastUpdateDate';
import ReportTableWrapper from '../ReportTable/ReportTableWrapper';
import SinglePlanSelector from '../SinglePlanSelector';
import ToggleSSNVisible from '../ToggleSSNVisible';
import TopButtonPortalWrapper from '../TopButtonPortalWrapper';

import { PlanOmnibusBalanceReportTableParams } from './PlanOmnibusBalanceReport.types';
import {
  useGetOmnibusPlansQuery,
  usePlanOmnibusBalanceReportExportCSVQuery,
  usePlanOmnibusBalanceReportQuery,
} from './queries';

interface Props extends ReportTableBaseProps {
  filtersValues: PlanOmnibusBalanceReportTableParams;
  onSetFilterValue: FilterSetters<PlanOmnibusBalanceReportTableParams>;
}

const PlanOmnibusBalanceReportTable: React.FC<Props> = ({
  filtersValues,
  onSetFilterValue,
  isPreviewMode,
  defaultEmployerId,
}) => {
  const [isAutoFocus, setAutoFocus] = useState(false);
  const [isPlanAutoFocus, setPlanAutoFocus] = useState(false);
  const debouncedSearch = useDebounce(filtersValues.searchString);
  const isEmployerIsPresent = useIsEmployerIsPresent(filtersValues);
  const {
    isHasNextPage,
    total,
    isLoading,
    headers,
    nodes,
    lastTimeUpdateDate,
    columnWidths,
    handleLoadMore,
    isExportDisabled,
    onChangeHeaderSize,
  } = usePlanOmnibusBalanceReportQuery({
    ...filtersValues,
    searchString: debouncedSearch,
    isPreviewMode,
  });
  const {
    options,
    isLoading: isPlanOptionLoading,
    isSuccess: isPlanOptionSuccess,
    isError: isPlanOptionsError,
  } = useGetOmnibusPlansQuery({
    organizationId: filtersValues.employerId,
  });
  const childrenNode = useMemo(() => {
    if (isEmployerIsPresent && !options.length && (isPlanOptionSuccess || isPlanOptionsError)) {
      return (
        <PlaceholderBase isLarge>
          <PlaceholderBase.Default />
        </PlaceholderBase>
      );
    }
    if (!isEmployerIsPresent || !filtersValues.reportPlanIds.length) {
      return (
        <PlaceholderBase>
          <Text data-testid="placeholder-base_text" size="large" margin={{ top: 'spacing24' }} weight={700}>
            Please select an{' '}
            {!isEmployerIsPresent ? (
              <Anchor
                data-testid="placeholder-base_link_employer"
                onClick={() => {
                  setAutoFocus(true);
                }}
              >
                employer
              </Anchor>
            ) : (
              'employer'
            )}{' '}
            and{' '}
            {!isEmployerIsPresent ? (
              'plan name'
            ) : (
              <Anchor
                data-testid="placeholder-base_link_plan"
                onClick={() => {
                  setPlanAutoFocus(true);
                }}
              >
                plan name
              </Anchor>
            )}{' '}
            to view the relevant data and insights
          </Text>
        </PlaceholderBase>
      );
    }

    if (!isLoading && !total) {
      if (filtersValues.searchString.trim()) {
        return (
          <PlaceholderBase isLarge>
            <PlaceholderBase.Search />
          </PlaceholderBase>
        );
      }
      return (
        <PlaceholderBase isLarge>
          <PlaceholderBase.Default />
        </PlaceholderBase>
      );
    }

    return (
      <VirtualTable
        testId="report-table"
        height={isPreviewMode ? 'calc(100vh - 545px)' : 'calc(80vh - 160px)'}
        isLoading={isLoading}
        onResizeHeaders={onChangeHeaderSize}
        onLoadMore={handleLoadMore}
        isHasMore={isHasNextPage}
        columnSizes={columnWidths}
        isIndexMode={true}
        headers={headers}
        items={nodes}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filtersValues.employerId,
    filtersValues.employerIds,
    filtersValues.searchString,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    filtersValues.reportPlanIds?.join(','),
    options.length,
    isPlanOptionSuccess,
    isPlanOptionsError,
    isLoading,
    total,
    isPreviewMode,
    onChangeHeaderSize,
    handleLoadMore,
    isHasNextPage,
    columnWidths,
    headers,
    nodes,
  ]);
  const { handleExport: handleExportCSV, isLoading: isExportLoading } =
    usePlanOmnibusBalanceReportExportCSVQuery(filtersValues);
  const handleShowAppliedFilterSnackbar = useShowAppliedSettingsSnackbar();
  const handleToggleSSN = useCallback(
    (value: boolean) => {
      onSetFilterValue.isSSNShowed(value);
      handleShowAppliedFilterSnackbar();
    },
    [handleShowAppliedFilterSnackbar, onSetFilterValue]
  );
  return (
    <Box direction="column">
      <TopButtonPortalWrapper
        isPreviewMode={isPreviewMode}
        infoNode={
          <ReportLastUpdateDate
            isWideMode
            isLoading={isLoading}
            key={`${filtersValues.employerId}`}
            value={lastTimeUpdateDate}
          />
        }
        buttonNode={
          <Box direction="row">
            <AddScheduledReportButton
              employerId={filtersValues.employerId}
              reportPlanIds={filtersValues.reportPlanIds}
              type={ReportTypeKeys.PlanOmnibusBalance}
              tab={ReportTab.Report}
            />
            <Box margin={{ left: 'spacing12' }}>
              <ExportButton
                isLoading={isExportLoading}
                onClick={handleExportCSV}
                disabled={!isEmployerIsPresent || isExportDisabled || !filtersValues.reportPlanIds.length}
                text="Download Report (CSV)"
              />
            </Box>
          </Box>
        }
      >
        <ReportTableWrapper
          total={total}
          isLoading={isLoading}
          isPreviewMode={isPreviewMode}
          headerChildren={
            isPreviewMode ? (
              <Box fill justify="end" align="end">
                <ToggleSSNVisible
                  disabled={!isEmployerIsPresent || !filtersValues.reportPlanIds.length}
                  value={filtersValues.isSSNShowed}
                  onChangeValue={handleToggleSSN}
                />
              </Box>
            ) : null
          }
          filters={
            <Box direction={isPreviewMode ? 'column' : 'row'} fill>
              <Grid
                columns={
                  isPreviewMode
                    ? {
                        count: 3,
                        size: 'auto',
                      }
                    : ['220px', '220px', '220px', '290px']
                }
                gap="spacing12"
              >
                {!isPreviewMode && (
                  <SearchInput
                    testId="report"
                    placeholder="Search"
                    value={filtersValues.searchString}
                    disabled={!isEmployerIsPresent}
                    onChange={onSetFilterValue.searchString}
                  />
                )}

                <EmployerSelector
                  disabled={isPreviewMode && !!defaultEmployerId}
                  onSetAutoFocus={setAutoFocus}
                  isAutoFocus={isAutoFocus}
                  value={filtersValues.employerId}
                  onChangeValue={onSetFilterValue.employerId}
                />

                <SinglePlanSelector
                  disabled={!isEmployerIsPresent || isPlanOptionLoading}
                  options={options}
                  isAutoFocus={isPlanAutoFocus}
                  onSetAutoFocus={setPlanAutoFocus}
                  value={filtersValues.reportPlanIds?.[0]}
                  onChangeValue={(id) => onSetFilterValue.reportPlanIds([id])}
                />
              </Grid>
              {!isPreviewMode && (
                <Box fill justify="end" align="end">
                  <ToggleSSNVisible
                    disabled={!isEmployerIsPresent || !filtersValues.reportPlanIds.length}
                    value={filtersValues.isSSNShowed}
                    onChangeValue={handleToggleSSN}
                  />
                </Box>
              )}
            </Box>
          }
        >
          <Box>{childrenNode}</Box>
        </ReportTableWrapper>
      </TopButtonPortalWrapper>
    </Box>
  );
};
export default PlanOmnibusBalanceReportTable;
